/**
 * 会话监控统一上报
 *
 * 使用单一 category（110）通过 msg 区分事件，覆盖连接、心跳、消息轮次、错误、交互卡片、
 * 会话发送全链路（创建/发送/排队/失败/接收终态）全场景。
 *
 * WPK 字段分配（同一字段在不同 msg 中含义不同，按 msg 查询时不会冲突）：
 *   c1  — connectionId
 *   c2  — conversationId
 *   c3  — sessionKey / projectKey(session_create 系列)
 *   c4  — 事件子类型 / 错误码 / visibilityState / 终态 state
 *   c5  — 失败阶段(chat_send_fail) / 发送来源 source(chat_abort) / 'user'(chat_send)
 *   c6  — 消息 ID / interactionId
 *   c7  — close code(disconnect) / 拒绝原因(file_reject)
 *   c8  — 流式异常标记(message_round) / 发送来源 source(chat_send 系列)
 *
 *   wl_avgv1 — 连接耗时 / 连接存活时长(disconnect) / 发送总耗时(chat_send)
 *   wl_avgv2 — ACK 耗时 / 文件上传耗时(chat_send)
 *   wl_avgv3 — 首 token 耗时 / 会话创建耗时(chat_session_create)
 *   wl_avgv4 — 响应完成耗时
 *   wl_avgv5 — 心跳 RTT / 最后 pong 距今(disconnect)
 *   wl_avgv6 — 附件数量(chat_send) / Agent 响应体大小(message_round) / 文件大小(file_reject)
 *   wl_avgv7 — 重连次数
 *   wl_avgv8 — message_data 推送条数
 *
 *   bl1 — 用户消息内容        bl4 — 错误详情 / 流式诊断
 *   bl2 — Agent 回复内容      bl5 — 文件名(file_reject) / 孤儿诊断
 *   bl3 — 异常上下文快照
 *
 * 所有方法内部做 try-catch 保护，确保监控异常不影响业务逻辑。
 */
import { itrace } from './itraceService';
import type { MessageRoundReport } from './wsReportCollector';

// ============== 常量 ==============

/** 统一 category：WebSocket 全场景监控 */
const WS_CATEGORY = 110;

// ============== 安全上报 ==============

function safeReport(log: Record<string, unknown>): void {
  try {
    itrace.report(log);
  } catch (err) {
    console.warn('[wsMonitor] report failed:', err);
  }
}

// ============== 连接事件 ==============

/** 连接建立成功 (ws_connect) */
export function reportWsConnect(
  connectionId: string,
  conversationId: string,
  sessionKey: string,
  connectTimeMs: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_connect',
    w_succ: 1,
    wl_avgv1: connectTimeMs,
    c1: connectionId,
    c2: conversationId,
    c3: sessionKey,
  });
}

/** 连接断开 (ws_disconnect) */
export function reportWsDisconnect(
  connectionId: string,
  conversationId: string,
  sessionKey: string,
  code: number,
  reason: string,
  aliveDurationMs?: number,
  visibilityState?: string,
  lastPongAgeMs?: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_disconnect',
    w_succ: code === 1000 ? 1 : 0,
    c1: connectionId,
    c2: conversationId,
    c3: sessionKey,
    c4: visibilityState,
    c7: String(code),
    wl_avgv1: aliveDurationMs,
    wl_avgv5: lastPongAgeMs,
    bl4: reason || undefined,
  });
}

/** 发起重连 (ws_reconnect) */
export function reportWsReconnect(
  connectionId: string,
  conversationId: string,
  attempt: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_reconnect',
    w_succ: 0,
    wl_avgv7: attempt,
    c1: connectionId,
    c2: conversationId,
  });
}

/** 重连耗尽 (ws_reconnect_exhausted) */
export function reportWsReconnectExhausted(
  connectionId: string,
  conversationId: string,
  maxAttempts: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_reconnect_exhausted',
    w_succ: 0,
    wl_avgv7: maxAttempts,
    c1: connectionId,
    c2: conversationId,
  });
}

// ============== 心跳事件 ==============

/** 心跳成功 (ws_heartbeat) */
export function reportWsHeartbeat(
  connectionId: string,
  conversationId: string,
  rttMs: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_heartbeat',
    w_succ: 1,
    wl_avgv5: rttMs,
    c1: connectionId,
    c2: conversationId,
  });
}

/** 心跳超时 (ws_pong_timeout) */
export function reportWsPongTimeout(
  connectionId: string,
  conversationId: string,
  visibilityState?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_pong_timeout',
    w_succ: 0,
    c1: connectionId,
    c2: conversationId,
    c4: visibilityState,
  });
}

// ============== 消息轮次（聚合上报） ==============

/** 消息交互轮次完成 (ws_message_round) — 由 WsReportCollector 调用 */
export function reportWsMessageRound(report: MessageRoundReport): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_message_round',
    w_succ: report.isSuccess ? 1 : 0,
    wl_avgv2: report.ackTimeMs,
    wl_avgv3: report.firstTokenTimeMs,
    wl_avgv4: report.completeTimeMs,
    wl_avgv6: report.agentResponseSize,
    wl_avgv8: report.messageDataCount,
    c1: report.connectionId,
    c2: report.conversationId,
    c3: report.sessionKey,
    c5: 'assistant',
    c6: report.msgId,
    c8: report.streamAnomalyFlag,
    bl1: report.userContent,
    bl2: report.agentContent,
    bl3: report.recentMessagesSnapshot,
    bl4: report.errorDetail
      ? report.streamDiagnostics
        ? `${report.errorDetail}\n${report.streamDiagnostics}`
        : report.errorDetail
      : report.streamDiagnostics,
  });
}

// ============== 错误事件 ==============

/** 连接错误 (ws_error_connect) */
export function reportWsErrorConnect(
  connectionId: string,
  conversationId: string,
  errorMsg: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_error_connect',
    w_succ: 0,
    c1: connectionId,
    c2: conversationId,
    bl4: errorMsg,
  });
}

/** 服务端错误 (ws_error_server) */
export function reportWsErrorServer(
  connectionId: string,
  conversationId: string,
  errorMsg: string,
  errorCode?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_error_server',
    w_succ: 0,
    c1: connectionId,
    c2: conversationId,
    c4: errorCode,
    bl4: errorMsg,
  });
}

// ============== 会话发送全链路监控 ==============
//
// 统一覆盖普通消息和交互卡片的发送，通过 source 字段区分来源。
//
// 字段复用：
//   c1  — connectionId
//   c2  — conversationId
//   c3  — sessionKey
//   c4  — 错误码 / 子阶段
//   c5  — 消息角色 / 失败阶段
//   c6  — 消息 ID / interactionId
//   c8  — 发送来源 source
//   wl_avgv1 — 发送总耗时 ms
//   wl_avgv2 — 文件上传耗时 ms
//   wl_avgv6 — 附件数量
//   bl1 — 用户消息内容（截取前 2000 字符）
//   bl4 — 错误详情

/** 发送来源类型：普通消息 / 排队 / 快捷按钮 / 交互卡片 / 交互表单 */
export type ChatSendSource =
  | 'user'
  | 'queued'
  | 'action_button'
  | 'interaction'
  | 'interaction_form';

/** 会话消息发送结果上下文 */
export interface ChatSendContext {
  connectionId: string;
  conversationId: string;
  sessionKey: string;
}

export type ChatSendContextPhase =
  | 'repair_connect'
  | 'mismatch_after_connect'
  | 'missing_target'
  | 'pending_enter'
  | 'pending_create_blocked'
  | 'pending_ws_connect'
  | 'session_select'
  | 'ws_wait_retry';
export type ChatPendingDropReason = 'session_switch' | 'ws_reconnect_exhausted' | 'user_cancel';

/** 交互响应发送前的会话上下文诊断 */
export interface ChatSendContextDiagnostics {
  targetConversationId?: string | null;
  targetProjectKey?: string;
  actualConversationId?: string | null;
  actualProjectKey?: string | null;
  activeDraftSessionId?: string | null;
  expectedDraftSessionId?: string | null;
  routeSessionId?: string | null;
  reason?: string;
  isPendingSession?: boolean;
  isQueuedSource?: boolean;
  isConnected?: boolean;
  detail?: string;
}

/** 会话消息发送成功 (chat_send) */
export function reportChatSend(
  ctx: ChatSendContext,
  durationMs: number,
  source: ChatSendSource,
  attachmentCount: number,
  uploadDurationMs?: number,
  userContent?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_send',
    w_succ: 1,
    wl_avgv1: durationMs,
    wl_avgv2: uploadDurationMs,
    wl_avgv6: attachmentCount,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
    c8: source,
    bl1: userContent?.slice(0, 2000),
  });
}

/** 会话消息发送失败 (chat_send_fail) */
export function reportChatSendFail(
  ctx: ChatSendContext,
  errorCode: string,
  phase: 'ws_connect' | 'upload' | 'ws_send' | 'server_reject',
  durationMs: number,
  source: ChatSendSource,
  errorDetail?: string,
  userContent?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_send_fail',
    w_succ: 0,
    wl_avgv1: durationMs,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
    c4: errorCode,
    c5: phase,
    c8: source,
    bl1: userContent?.slice(0, 2000),
    bl4: errorDetail,
  });
}

/** 交互响应发送上下文诊断 (chat_send_context) */
export function reportChatSendContext(
  ctx: ChatSendContext,
  phase: ChatSendContextPhase,
  source: ChatSendSource,
  diagnostics: ChatSendContextDiagnostics,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_send_context',
    w_succ: phase === 'repair_connect' ? 1 : 0,
    c1: ctx.connectionId,
    c2: diagnostics.targetConversationId ?? ctx.conversationId,
    c3: ctx.sessionKey,
    c4: phase,
    c5: source,
    c6: diagnostics.actualConversationId ?? undefined,
    c7: diagnostics.actualProjectKey ?? undefined,
    c8: diagnostics.targetProjectKey,
    bl3: JSON.stringify({
      targetConversationId: diagnostics.targetConversationId,
      targetProjectKey: diagnostics.targetProjectKey,
      actualConversationId: diagnostics.actualConversationId,
      actualProjectKey: diagnostics.actualProjectKey,
      activeDraftSessionId: diagnostics.activeDraftSessionId,
      expectedDraftSessionId: diagnostics.expectedDraftSessionId,
      routeSessionId: diagnostics.routeSessionId,
      reason: diagnostics.reason,
      isPendingSession: diagnostics.isPendingSession,
      isQueuedSource: diagnostics.isQueuedSource,
      isConnected: diagnostics.isConnected,
      detail: diagnostics.detail,
    }),
  });
}

/** 排队交互响应被丢弃 (chat_pending_drop) */
export function reportChatPendingDrop(
  ctx: ChatSendContext,
  reason: ChatPendingDropReason,
  source: ChatSendSource,
  diagnostics: {
    targetConversationId?: string;
    targetProjectKey?: string;
    activeConversationId?: string;
    pendingId?: string;
    inFlight?: boolean;
  },
  userContent?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_pending_drop',
    w_succ: 0,
    c1: ctx.connectionId,
    c2: diagnostics.targetConversationId ?? ctx.conversationId,
    c3: ctx.sessionKey,
    c4: reason,
    c5: source,
    c6: diagnostics.pendingId,
    c7: diagnostics.activeConversationId,
    c8: diagnostics.targetProjectKey,
    bl1: userContent?.slice(0, 2000),
    bl3: JSON.stringify(diagnostics),
  });
}

/** 消息排队 (chat_queued)：Agent 执行中时用户消息进入排队 */
export function reportChatQueued(
  ctx: ChatSendContext,
  attachmentCount: number,
  userContent?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_queued',
    w_succ: 1,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
    wl_avgv6: attachmentCount,
    bl1: userContent?.slice(0, 2000),
  });
}

/** 会话创建失败 (chat_session_create_fail) */
export function reportChatSessionCreateFail(
  projectKey: string,
  errorDetail: string,
  conversationId?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_session_create_fail',
    w_succ: 0,
    c2: conversationId,
    c3: projectKey,
    bl4: errorDetail,
  });
}

/** 会话创建成功 (chat_session_create) */
export function reportChatSessionCreate(
  projectKey: string,
  conversationId: string,
  durationMs: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_session_create',
    w_succ: 1,
    wl_avgv3: durationMs,
    c2: conversationId,
    c3: projectKey,
  });
}

/** 文件上传失败 (chat_upload_fail) */
export function reportChatUploadFail(
  ctx: ChatSendContext,
  errorDetail: string,
  fileCount: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_upload_fail',
    w_succ: 0,
    wl_avgv6: fileCount,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
    bl4: errorDetail,
  });
}

/** WS 接收终态事件 (chat_receive_terminal)：观测 Agent 回复的终态分布 */
export function reportChatReceiveTerminal(
  connectionId: string,
  conversationId: string,
  sessionKey: string,
  state: string,
  messageId: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_receive_terminal',
    w_succ: state === 'final' || state === 'segment_end' ? 1 : 0,
    c1: connectionId,
    c2: conversationId,
    c3: sessionKey,
    c4: state,
    c6: messageId,
  });
}

// ============== 用户操作事件 ==============

/** 用户取消 Agent 执行 (chat_abort) */
export function reportChatAbort(
  ctx: ChatSendContext,
  runId: string,
  /** 从发送到取消的耗时 ms（即 Agent 运行了多久被取消） */
  elapsedSinceSendMs?: number,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_abort',
    w_succ: 1,
    wl_avgv1: elapsedSinceSendMs,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
    c6: runId,
  });
}

/** 文件校验拒绝 (chat_file_reject)：文件大小超限或类型不支持 */
export function reportChatFileReject(
  reason: 'size_exceeded' | 'type_unsupported',
  fileName: string,
  fileSize: number,
  fileType: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_file_reject',
    w_succ: 0,
    c7: reason,
    c4: fileType,
    wl_avgv6: fileSize,
    bl5: fileName,
  });
}

/** 30 分钟安全重置 (chat_send_safety_reset)：isSending 卡死兜底触发 */
export function reportChatSendSafetyReset(ctx: ChatSendContext): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'chat_send_safety_reset',
    w_succ: 0,
    c1: ctx.connectionId,
    c2: ctx.conversationId,
    c3: ctx.sessionKey,
  });
}

// ============== 连接异常诊断 ==============

/**
 * 断连时孤儿 tracker/pendingRound 补报 (ws_orphan_round)：
 * 连接断开时仍在等待终态的消息轮次，上报其已收集到的部分数据用于排障。
 */
export function reportWsOrphanRound(
  connectionId: string,
  conversationId: string,
  sessionKey: string,
  orphanType: 'tracker' | 'pending_round',
  diagnostics: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_orphan_round',
    w_succ: 0,
    c1: connectionId,
    c2: conversationId,
    c3: sessionKey,
    c4: orphanType,
    bl5: diagnostics,
  });
}

// ============== WS 操作级失败 ==============

/**
 * WS 底层操作失败 (ws_op_fail)：
 * 覆盖 sendMessage/abortCurrentRun/sendPing 等操作的静默失败分支，
 * 让每一次"前端没发出去"都有迹可查。
 *
 * 字段分配：
 *   c4  — 操作类型 operation: send_message / abort / ping / connect_precondition / parse_message
 *   c7  — 失败原因 reason: not_connected / ws_null / send_exception / parse_error / missing_params
 *   bl4 — 错误详情（异常 message 或原始数据摘要）
 *   bl1 — 用户消息内容摘要（仅 send_message 时填充）
 */
export type WsOperation =
  | 'send_message'
  | 'abort'
  | 'ping'
  | 'connect_precondition'
  | 'parse_message';
export type WsFailReason =
  | 'not_connected'
  | 'ws_null'
  | 'send_exception'
  | 'parse_error'
  | 'missing_params';

export function reportWsOpFail(
  connectionId: string,
  conversationId: string,
  operation: WsOperation,
  reason: WsFailReason,
  errorDetail?: string,
  userContentPreview?: string,
): void {
  safeReport({
    category: WS_CATEGORY,
    msg: 'ws_op_fail',
    w_succ: 0,
    c1: connectionId,
    c2: conversationId,
    c4: operation,
    c7: reason,
    bl4: errorDetail,
    bl1: userContentPreview?.slice(0, 500),
  });
}
